import Util from '../../shared/util/util';
import DirectApi from '../../../directApi';

export const INVOICE_INTEGRATIONS = {
	XERO: 'XERO',
	ECONOMIC: 'ECONOMIC',
	QUICKBOOKS: 'QUICKBOOKS',
};

export type Contact = {
	id: string;
	name: string;
	address?: string;
	taxId?: string;
	integrationInstanceId?: string;
	integration: string;
};

export type EnabledInvoiceIntegrations = {
	readonly newXeroEnabled?: boolean | null;
	readonly economicEnabled?: boolean | null;
	readonly quickbooksEnabled?: boolean | null;
};

export class ContactsFetchError extends Error {
	readonly integration: string;
	readonly correlationId?: string;

	constructor(integration: string, correlationId?: string) {
		super(`Failed to fetch ${integration} contacts`);
		this.name = 'ContactsFetchError';
		this.integration = integration;
		this.correlationId = correlationId;
	}
}

type IntegrationCustomer = {
	id: string | number;
	name: string;
	taxId?: string | number;
	address?: string;
	integrationInstanceId?: string;
};

const defaultContactsMapper = (customers: IntegrationCustomer[], integrationName: string): Contact[] =>
	customers
		.sort((a, b) => Util.sortAlphabetically(a.name, b.name))
		.map(customer => ({
			id: String(customer.id),
			name: customer.name,
			taxId: String(customer.taxId),
			address: customer.address,
			integrationInstanceId: customer.integrationInstanceId,
			integration: integrationName,
		}));

const fetchContacts = (endpoint: string, integration: string): Promise<Contact[]> =>
	DirectApi.Fetch_Raw(endpoint).then(async (response: Response) => {
		// QuickBooks answers 404 rather than an empty list when a connected company has no customers.
		if (response.status === 404) {
			return [];
		}
		const body = await response.json().catch(() => null);
		if (!response.ok || !Array.isArray(body)) {
			throw new ContactsFetchError(integration, body?.correlationId);
		}
		return defaultContactsMapper(body, integration);
	});

const fetchXeroContacts = (): Promise<Contact[]> => fetchContacts('xero/allContacts', INVOICE_INTEGRATIONS.XERO);
const fetchQuickbooksContacts = (): Promise<Contact[]> =>
	fetchContacts('quickbooks/all_customers', INVOICE_INTEGRATIONS.QUICKBOOKS);
const fetchEconomicContacts = (): Promise<Contact[]> => fetchContacts('economic/all_customers', INVOICE_INTEGRATIONS.ECONOMIC);

type ContactsResult = {contacts: Contact[]} | {error: unknown};

/**
 * Fetches contacts only for the integrations the company has connected. A failing integration is
 * skipped so the healthy ones can still be used; it rejects only when every enabled integration
 * fails, with the first failure in Xero, QuickBooks, e-conomic order.
 */
export const fetchEnabledIntegrationContacts = (enabled: EnabledInvoiceIntegrations): Promise<Contact[]> => {
	const fetches = [
		enabled.newXeroEnabled ? fetchXeroContacts : null,
		enabled.quickbooksEnabled ? fetchQuickbooksContacts : null,
		enabled.economicEnabled ? fetchEconomicContacts : null,
	].filter((fetcher): fetcher is () => Promise<Contact[]> => fetcher !== null);

	return Promise.all(
		fetches.map(fetcher =>
			fetcher().then(
				(contacts): ContactsResult => ({contacts}),
				(error: unknown): ContactsResult => ({error})
			)
		)
	).then(results => {
		const succeeded = results.flatMap(result => ('contacts' in result ? [result.contacts] : []));
		const firstFailure = results.find((result): result is {error: unknown} => 'error' in result);
		if (firstFailure && succeeded.length === 0) {
			throw firstFailure.error;
		}
		return succeeded.flat();
	});
};

export const filterContactsByIntegrationInstanceId = (
	contacts: Contact[],
	billFrom?: {
		integration?: string;
		externalId?: string;
	} | null
): Contact[] =>
	contacts.filter(
		contact =>
			(!contact.integrationInstanceId || contact.integrationInstanceId === billFrom?.externalId) &&
			contact.integration === billFrom?.integration
	);
